import {
  featureLabel,
  policyLabel,
  ruleSourceLabel,
  untilLabel,
} from '@app/src/pages/Configurations/ConfigurationsPages/Organization/Access/accessMatrix'
import type { EntitlementPolicy, ScopeEntitlement } from '@app/src/types/entitlements'
import { AccessPolicy, EntitlementFeature } from '@app/src/types/entitlements'
import type { SolutionType } from '@app/src/types/resourceExplorer'
import { capitalize } from '@app/src/utils/helpers'

export const questionnaireFeatures = [EntitlementFeature.Questionnaire, EntitlementFeature.AiScreening]
export const assessmentFeatures = [EntitlementFeature.Assessment]

export type PolicyEdits = Partial<Record<EntitlementFeature, AccessPolicy>>

export const policyDescription: Record<AccessPolicy, string> = {
  [AccessPolicy.Included]: 'Every organization can use it.',
  [AccessPolicy.AddOn]: 'Every organization sees it, locked until CS grants it.',
  [AccessPolicy.Hidden]: 'Only organizations CS grants it to see it.',
}

export const policyFor = (
  policies: EntitlementPolicy[],
  feature: EntitlementFeature,
  scopeId: number,
): EntitlementPolicy => {
  const savedPolicy = policies.find(policy => policy.feature === feature && policy.scopeId === scopeId)
  return savedPolicy ?? { feature, scopeId, policy: AccessPolicy.Included, isDefaultScope: false }
}

export const shownPolicy = (
  policies: EntitlementPolicy[],
  feature: EntitlementFeature,
  scopeId: number,
  edits: PolicyEdits,
): AccessPolicy => edits[feature] ?? policyFor(policies, feature, scopeId).policy

export const changedPolicies = (
  policies: EntitlementPolicy[],
  features: EntitlementFeature[],
  scopeId: number,
  edits: PolicyEdits,
): EntitlementPolicy[] =>
  features.flatMap(feature => {
    const saved = policyFor(policies, feature, scopeId)
    const edited = edits[feature]
    return edited && edited !== saved.policy ? [{ ...saved, policy: edited }] : []
  })

export const policySummary = (policies: EntitlementPolicy[], features: EntitlementFeature[], scopeId: number) =>
  features
    .map(feature => {
      const { policy } = policyFor(policies, feature, scopeId)
      return `${capitalize(featureLabel[feature])}: ${policyLabel[policy]}`
    })
    .join(' · ')

export const ruleDescription = (rule: ScopeEntitlement, solutionLabel: (solutionType: SolutionType) => string) =>
  [
    ruleSourceLabel(rule),
    rule.solutionType && `${solutionLabel(rule.solutionType)} only`,
    rule.validUntil && untilLabel(rule.validUntil),
    rule.note,
  ]
    .filter(Boolean)
    .join(' · ')
